import React, { useEffect, useState } from "react";
import { Card, Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import Chart from "react-apexcharts";
import { API_ENDPOINTS } from "../../../../config/api";

const TotalRevenue = ({ data }) => {
  const [isChartLoaded, setChartLoaded] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [revenueCount, setRevenueCount] = useState(0);
  const [revenueData, setRevenueData] = useState([""]);
  const [chartData, setChartData] = useState({
    series: [{
      name: 'Revenue',
      data: []
    }],
    options: {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100]
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      markers: {
        size: 0,
        discrete: [],
        hover: {
          size: 7,
        }
      },
      xaxis: {
        categories: [],
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        labels: {
          formatter: function(value) {
            return new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          }
        }
      },
      tooltip: {
        y: {
          formatter: function(value) {
            return new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value);
          }
        }
      },
      colors: ['#2196F3']
    }
  });

  // Function to format amount in Indian Rupees
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // This useEffect seems to be for initial data fetch, possibly not day-wise. Keeping for now.
  // useEffect(() => {
  //   const fetchRevenewData = async () => {
  //     try {
  //       // const response = await axios.get(API_ENDPOINTS.DAY_WISE_REVENEW);
  //       // setDashboardData(response.data);
  //     } catch (error) {
  //       console.error("Error fetching Total Revenew data:", error);
  //     }
  //   };
  //   fetchRevenewData();
  // }, []);

  // Combined data fetching for initial load and period changes
  useEffect(() => {
    setChartLoaded(true);
    const fetchRevenueData = async () => {
      try {
        console.log('Fetching data for period:', selectedPeriod);
        // Call API with selected period
        const response = await fetch(`${API_ENDPOINTS.DAY_WISE_REVENEW}?period=${selectedPeriod}`);
        const data = await response.json();
        
        console.log('API response data:', data);

        if (data.revenueReport) {
          console.log('Revenue report data:', data.revenueReport);
          // Use rangetotal from API response for total revenue display
          setRevenueCount(data.rangetotal || 0);

          // Update chart data using 'label' and 'total' from revenueReport
          const categories = data.revenueReport.map(item => item.label);
          const seriesData = data.revenueReport.map(item => item.total);

          // Create discrete markers for non-zero data points
          const discreteMarkers = data.revenueReport.map((item, index) => {
            if (item.total > 0) {
              return {
                seriesIndex: 0, // Assuming only one series
                dataPointIndex: index,
                fillColor: '#FF5722', // Different color marker
                strokeColor: '#fff',
                size: 5
              };
            }
            return undefined; // No marker for zero values
          }).filter(marker => marker !== undefined); // Filter out undefined entries
          
          console.log('Chart categories:', categories);
          console.log('Chart series data:', seriesData);
          console.log('Discrete markers:', discreteMarkers);

          setChartData(prev => ({
            ...prev,
            series: [{
              name: 'Revenue',
              data: seriesData
            }],
            options: {
              ...prev.options,
              xaxis: {
                ...prev.options.xaxis,
                categories: categories
              },
              markers: {
                ...prev.options.markers,
                discrete: discreteMarkers
              }
            }
          }));
        } else {
          console.log('API response does not contain revenueReport.', data);
          setRevenueCount(0);
          setChartData(prev => ({
            ...prev,
            series: [{
              name: 'Revenue',
              data: []
            }],
            options: {
              ...prev.options,
              xaxis: {
                ...prev.options.xaxis,
                categories: []
              },
              markers: {
                ...prev.options.markers,
                discrete: []
              }
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        setRevenueCount(0);
        setChartData(prev => ({
          ...prev,
          series: [{
            name: 'Revenue',
            data: []
          }],
          options: {
            ...prev.options,
            xaxis: {
              ...prev.options.xaxis,
              categories: []
            },
            markers: {
              ...prev.options.markers,
              discrete: []
            }
          }
        }));
      }
    };

    fetchRevenueData();

  }, [selectedPeriod]); // Fetch data when selectedPeriod changes

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
        className="rmui-card"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: "25px",
            flexDirection: 'column',
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: "4px",
                  gap: "10px",
                }}
              >
                <Typography component="span">Total Revenue</Typography>
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                }}
                className="text-black"
              >
                {formatAmount(revenueCount)}
              </Typography>
            </Box>

            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                size="small"
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {isChartLoaded && (
            <Chart
              options={chartData.options}
              series={chartData.series}
              type="line"
              height={350}
              width={"100%"}
            />
          )}
        </Box>
      </Card>
    </>
  );
};

export default TotalRevenue;